import { useAnalyticsFilters, AnalyticsFilters } from './AnalyticsFiltersContext'

type ArrayFilterKey = 'cities' | 'districts' | 'typologies'

type NumericFilterKey =
  | 'min_price'
  | 'max_price'
  | 'min_sqm'
  | 'max_sqm'

/**
 * Thin helper around `useAnalyticsFilters` that exposes the two mutation
 * patterns chart click-handlers need:
 *
 * - `toggleIn(key, value)` — add/remove a single value from a multi-select
 *   array filter (cities / districts / typologies). Used by bar chart,
 *   pie chart and heatmap click-to-filter handlers.
 * - `setRange(minKey, maxKey, min, max)` — set a numeric [min, max] pair in
 *   one go (price / m² sliders, histogram bucket click).
 *
 * Intent: keep chart components free of filter-state plumbing so they only
 * have to know which logical dimension their click represents.
 */
export function useFilterDispatch() {
  const { filters, setFilters } = useAnalyticsFilters()

  const toggleIn = (key: ArrayFilterKey, value: string) => {
    if (!value) return
    const current = filters[key]
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    setFilters({ [key]: next } as Partial<AnalyticsFilters>)
  }

  const setRange = (
    minKey: NumericFilterKey,
    maxKey: NumericFilterKey,
    min: number | null,
    max: number | null,
  ) => {
    setFilters({ [minKey]: min, [maxKey]: max } as Partial<AnalyticsFilters>)
  }

  return { toggleIn, setRange, filters }
}
